// Version du LECTEUR. Une app installée ne sait jouer que les objets et
// actions qu'elle connaissait à sa sortie : chaque document publié porte la
// version minimale du lecteur qu'il exige (`runtime_min`), calculée ici. Un
// lecteur plus ancien affiche « Mets à jour l'application » au lieu d'une
// animation cassée (même mécanique que `engine_version` des feuilles de
// brouillon).
//
// Ajouter un objet ou une action : l'ajouter au schéma, ici avec la version
// du lecteur qui le sait jouer, et monter `JRAF_RUNTIME_VERSION`.

import type { JrafDocument } from './schema/document';
import type { JrafObjectType } from './schema/objects';
import type { JrafAction } from './schema/steps';

/** Version du lecteur de ce paquet. */
export const JRAF_RUNTIME_VERSION = 3;

/**
 * Ce que chaque version du lecteur a ajouté :
 *   1 — le catalogue de départ ;
 *   2 — cadre `box` des textes, `speaker` des voix enregistrées, `bookends`
 *       (voix de l'intro et du générique) ;
 *   3 — `mentions` (l'objet nommé par la voix s'éclaire), l'action
 *       `object.highlight` et l'accroche de la vidéo (`bookends.hook`).
 */
export const FEATURE_RUNTIME = {
  box: 2,
  speaker: 2,
  bookends: 2,
  mentions: 3,
  hook: 3,
} as const;

export const OBJECT_RUNTIME: Record<JrafObjectType, number> = {
  rect: 1,
  circle: 1,
  ellipse: 1,
  line: 1,
  arrow: 1,
  polygon: 1,
  path: 1,
  point: 1,
  text: 1,
  formula: 1,
  value: 1,
  image: 1,
  group: 1,
};

export const ACTION_RUNTIME: Record<JrafAction, number> = {
  'object.show': 1,
  'object.hide': 1,
  'object.move': 1,
  'object.scale': 1,
  'object.rotate': 1,
  'object.style': 1,
  'label.set': 1,
  'formula.transform': 1,
  'value.set': 1,
  'object.highlight': 3,
  'timeline.wait': 1,
  'question.ask': 1,
  'tool.open': 1,
};

/** Version minimale du lecteur exigée par un document validé. */
export function runtimeMinOf(
  doc: Pick<JrafDocument, 'objects' | 'scenes'> &
    Partial<Pick<JrafDocument, 'assets' | 'bookends'>>,
): number {
  let v = 1;
  for (const o of doc.objects) {
    v = Math.max(v, OBJECT_RUNTIME[o.type]);
    if ('box' in o && o.box) v = Math.max(v, FEATURE_RUNTIME.box);
    if (o.mentions?.length) v = Math.max(v, FEATURE_RUNTIME.mentions);
  }
  for (const a of doc.assets ?? [])
    if (a.kind === 'audio' && a.speaker) v = Math.max(v, FEATURE_RUNTIME.speaker);
  if (doc.bookends) v = Math.max(v, FEATURE_RUNTIME.bookends);
  if (doc.bookends?.hook || doc.bookends?.hookAudioAssetId) v = Math.max(v, FEATURE_RUNTIME.hook);
  for (const s of doc.scenes) for (const st of s.steps) v = Math.max(v, ACTION_RUNTIME[st.action]);
  return v;
}

/** Ce lecteur sait-il jouer ce document ? */
export function canPlay(runtimeMin: number, runtimeVersion = JRAF_RUNTIME_VERSION): boolean {
  return Number.isInteger(runtimeMin) && runtimeMin >= 1 && runtimeMin <= runtimeVersion;
}
